import { z } from 'zod';

export const MarketingCampaignTypeSchema = z.enum(['BROADCAST', 'SEQUENCE']);
export const MarketingCampaignStatusSchema = z.enum([
  'DRAFT',
  'SCHEDULED',
  'RUNNING',
  'PAUSED',
  'COMPLETED',
  'CANCELLED',
]);

export const MarketingAudienceFilterSchema = z.object({
  registeredFrom: z.string().datetime().optional(),
  registeredTo: z.string().datetime().optional(),
  tenantIds: z.array(z.string().uuid()).max(100).optional(),
  gymIds: z.array(z.string().uuid()).max(100).optional(),
  lastBookingFrom: z.string().datetime().optional(),
  lastBookingTo: z.string().datetime().optional(),
  membershipTiers: z.array(z.enum(['BASIC', 'PREMIUM', 'ELITE'])).max(3).optional(),
  membershipStatuses: z.array(z.enum(['ACTIVE', 'INACTIVE', 'CANCELLED'])).max(3).optional(),
});

export const MarketingCampaignStepInputSchema = z.object({
  position: z.number().int().min(0).max(49),
  delayMinutes: z.number().int().min(0).max(525_600),
  subject: z.string().trim().min(1).max(200),
  htmlBody: z.string().trim().min(1).max(500_000),
  textBody: z.string().trim().min(1).max(100_000),
});

const MarketingCampaignDraftSchema = z.object({
    name: z.string().trim().min(1).max(120),
    type: MarketingCampaignTypeSchema,
    audienceFilter: MarketingAudienceFilterSchema.default({}),
    steps: z.array(MarketingCampaignStepInputSchema).min(1).max(50),
  });

function validateCampaignShape(
  value: { type?: 'BROADCAST' | 'SEQUENCE' | undefined; steps?: Array<{ position: number; delayMinutes: number }> | undefined },
  ctx: z.RefinementCtx,
) {
    if (!value.steps) return;
    const positions = value.steps.map((step) => step.position).sort((a, b) => a - b);
    if (positions.some((position, index) => position !== index)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['steps'], message: 'Step positions must be contiguous from zero' });
    }
    if (value.steps[0]?.delayMinutes !== 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['steps', 0, 'delayMinutes'], message: 'The first step delay must be zero' });
    }
    if (value.type === 'BROADCAST' && value.steps.length !== 1) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['steps'], message: 'Broadcasts require exactly one step' });
    }
}

export const CreateMarketingCampaignInputSchema = MarketingCampaignDraftSchema.superRefine(validateCampaignShape);
export const UpdateMarketingCampaignInputSchema = MarketingCampaignDraftSchema.partial().superRefine(validateCampaignShape);
export const MarketingAudiencePreviewInputSchema = z.object({
  audienceFilter: MarketingAudienceFilterSchema.default({}),
});
export const ScheduleMarketingCampaignInputSchema = z.object({
  scheduledAt: z.string().datetime(),
});
export const MarketingUnsubscribeInputSchema = z.object({ token: z.string().min(32).max(256) });

export const ProgramLeadInputSchema = z.object({
  firstName: z.string().trim().min(1).max(100),
  lastName: z.string().trim().min(1).max(100),
  email: z.string().trim().email().max(254),
  phone: z.string().trim().min(6).max(40),
  city: z.string().trim().min(1).max(100),
  preferredCallTime: z.enum(['MORNING', 'AFTERNOON', 'EVENING', 'ANYTIME']),
  program: z.enum(['INTRO', 'TRANSFORMATION_6', 'TRANSFORMATION_12']),
  consent: z.literal(true),
  website: z.string().max(200).optional(),
});

export type MarketingAudienceFilter = z.infer<typeof MarketingAudienceFilterSchema>;
export type MarketingCampaignStepInput = z.infer<typeof MarketingCampaignStepInputSchema>;
export type CreateMarketingCampaignInput = z.infer<typeof CreateMarketingCampaignInputSchema>;
export type UpdateMarketingCampaignInput = z.infer<typeof UpdateMarketingCampaignInputSchema>;
export type MarketingAudiencePreviewInput = z.infer<typeof MarketingAudiencePreviewInputSchema>;
export type ScheduleMarketingCampaignInput = z.infer<typeof ScheduleMarketingCampaignInputSchema>;
export type ProgramLeadInput = z.infer<typeof ProgramLeadInputSchema>;
